import React, {
  KeyboardEvent, useEffect, useRef, useState,
} from 'react';
import { KatButton, KatInput, KatModal } from '@amzn/katal-react';
import { createPortal } from 'react-dom';
import { useSlate } from 'slate-react';
import { Editor, Location, Node } from 'slate';
import { useTranslation } from 'src/utils';
import { LinkElement } from 'src/types/CustomSlateTypes';
import hasMulipleBlocksSelected from './hasMulipleBlocksSelected';

export type AddLinkModalProps = {
  onClose: () => void
  onSave: (values: { text: string, url: string }) => void,
  linkElement?: LinkElement
};

const AddLinkModal = ({ onClose, onSave, linkElement }: AddLinkModalProps) => {
  const { t } = useTranslation();
  const editor = useSlate();
  const [values, setValues] = useState({
    text: (
      (linkElement && Node.string(linkElement))
      || (editor.selection && Editor.string(editor, editor.selection as Location))
      || '').trim(),
    url: (linkElement?.url || '').trim(),
  });
  const textInputRef = useRef<KatInput.Element>(null);
  const urlInputRef = useRef<KatInput.Element>(null);
  const saveDisabled = values.url.length === 0;

  const handleChange = (e: KatInput.InputEvent) => {
    const { name, value } = e.target as HTMLInputElement;
    setValues({ ...values, [name]: value.trim() });
  };

  const handleSave = () => {
    if (!saveDisabled) {
      onSave({
        text: values.text.trim(),
        url: values.url.trim(),
      });
      onClose();
    }
  };

  const handleKeyDown = (e: KatInput.KeydownEvent | KeyboardEvent<HTMLInputElement>) => {
    const { key } = e as KeyboardEvent<HTMLInputElement>;
    if (key === 'Enter') {
      handleSave();
    }
  };

  useEffect(() => {
    // KatInputs do not focus when using prop autofocus, Katal team is aware in the SIM below
    // For now we will have to use refs
    // https://issues.amazon.com/issues/KDS-2835
    setTimeout(() => {
      if (values.text.length === 0 && textInputRef.current) textInputRef.current.focus();
      else if (urlInputRef.current) urlInputRef.current.focus();
    }, 0);
  }, []);

  return createPortal(
    <KatModal
      data-testid="add-link-modal"
      title={t('link_header')}
      visible
      onClose={onClose}
    >
      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSave();
        }}
      >
        {!hasMulipleBlocksSelected(editor) && (
          <KatInput
            label={t('text_label') as string}
            name="text"
            onInput={handleChange}
            // @ts-ignore
            onKeyDown={handleKeyDown}
            value={values.text}
            ref={textInputRef}
          />
        )}
        <KatInput
          label={t('link_label') as string}
          name="url"
          onInput={handleChange}
          // @ts-ignore
          onKeyDown={handleKeyDown}
          value={values.url}
          ref={urlInputRef}
        />
        <div className="flex justify-end space-x-4 rtl:space-x-reverse">
          <KatButton
            data-testid="close-button"
            variant="secondary"
            onClick={onClose}
          >
            {t('close')}
          </KatButton>
          <KatButton
            data-testid="save-button"
            type="submit"
            disabled={saveDisabled}
          >
            {t('save')}
          </KatButton>
        </div>
      </form>
    </KatModal>,
    document.querySelector('#seller-forums') as Element,
  );
};

export default AddLinkModal;
